import React, { createContext, useContext, useEffect, useReducer } from 'react'
import allytm from '@ally/allytm'
import { CommunityPropertyState, IraPlan } from '../types'
import { API_STATUS } from '../utils/constants'
import { useIraBeneficiariesApi } from '../api/ira-beneficiaries-api'

export interface BeneficiariesProps {
  iraPlans: IraPlan[]
  getIraBeneficiaries: () => Promise<IraPlan[] | undefined>
  plansUpdating: boolean
  plansLoading: boolean
  hasPlansError: boolean
  isCommunityPropertyState: boolean
}

interface IBeneficiariesState {
  iraPlans: {
    data: IraPlan[]
    status: API_STATUS
  }
  communityPropertyState: {
    communityPropertyStateFlag: boolean
    status: API_STATUS
  }
}

type IReducerAction =
  | {
      type: 'SET_BENEFICIARIES'
      iraPlans: IraPlan[]
    }
  | {
      type: 'SET_BENEFICIARIES_STATUS'
      status: API_STATUS
    }
  | {
      type: 'SET_COMMUNITY_PROPERTY_STATE'
      communityPropertyStateFlag: boolean
    }
  | {
      type: 'SET_COMMUNITY_PROPERTY_STATE_STATUS'
      status: API_STATUS
    }

export const IraBeneficiariesContext = createContext({} as BeneficiariesProps)

export const iraBeneficiariesReducer = (
  state: IBeneficiariesState,
  action: IReducerAction,
): IBeneficiariesState => {
  switch (action.type) {
    case 'SET_BENEFICIARIES_STATUS':
      return {
        ...state,
        iraPlans: { ...state.iraPlans, status: action.status },
      }
    case 'SET_BENEFICIARIES':
      return {
        ...state,
        iraPlans: { ...state.iraPlans, data: action.iraPlans },
      }
    case 'SET_COMMUNITY_PROPERTY_STATE_STATUS':
      return {
        ...state,
        communityPropertyState: {
          ...state.communityPropertyState,
          status: action.status,
        },
      }
    case 'SET_COMMUNITY_PROPERTY_STATE':
      return {
        ...state,
        communityPropertyState: {
          ...state.communityPropertyState,
          communityPropertyStateFlag: action.communityPropertyStateFlag,
        },
      }
    default: {
      return state
    }
  }
}

export const IraBeneficiariesProvider: React.FC = ({ children }) => {
  const api = useIraBeneficiariesApi()

  const [state, dispatch] = useReducer(iraBeneficiariesReducer, {
    iraPlans: {
      status: API_STATUS.IDLE,
      data: [],
    },
    communityPropertyState: {
      status: API_STATUS.IDLE,
      communityPropertyStateFlag: false,
    },
  })

  const getIraBeneficiaries = async (): Promise<IraPlan[] | undefined> => {
    dispatch({ type: 'SET_BENEFICIARIES_STATUS', status: API_STATUS.PENDING })
    try {
      const iraPlans = await api.fetchIraBeneficiaries()
      dispatch({ type: 'SET_BENEFICIARIES', iraPlans })
      dispatch({
        type: 'SET_BENEFICIARIES_STATUS',
        status: API_STATUS.RESOLVED,
      })
      return iraPlans
    } catch (e: any) {
      dispatch({
        type: 'SET_BENEFICIARIES_STATUS',
        status: API_STATUS.REJECTED,
      })
      allytm.event(
        'customError',
        'It’s us – we’re having a moment. We couldn’t load your beneficiaries right now. We’ll be quick about it, so check back in a bit.',
        '',
      )
      return undefined
    }
  }

  const getCommunityPropertyState = async (): Promise<
    CommunityPropertyState | undefined
  > => {
    dispatch({
      type: 'SET_COMMUNITY_PROPERTY_STATE_STATUS',
      status: API_STATUS.PENDING,
    })
    try {
      const communityPropertyState = await api.fetchCommunityPropertyState()
      const { communityPropertyStateFlag } = communityPropertyState
      dispatch({
        type: 'SET_COMMUNITY_PROPERTY_STATE',
        communityPropertyStateFlag,
      })
      dispatch({
        type: 'SET_COMMUNITY_PROPERTY_STATE_STATUS',
        status: API_STATUS.RESOLVED,
      })
      return communityPropertyState
    } catch (e: any) {
      dispatch({
        type: 'SET_COMMUNITY_PROPERTY_STATE_STATUS',
        status: API_STATUS.REJECTED,
      })
      // istanbul ignore next - tagging
      allytm.event(
        'customError',
        'It’s us – we’re having a moment. We couldn’t load your beneficiaries right now. We’ll be quick about it, so check back in a bit.',
        '',
      )
      return undefined
    }
  }

  useEffect(() => {
    getIraBeneficiaries()
    getCommunityPropertyState()
  }, [])

  return (
    <IraBeneficiariesContext.Provider
      value={{
        // General data
        iraPlans: state.iraPlans.data,
        isCommunityPropertyState:
          state.communityPropertyState.communityPropertyStateFlag,

        // Error/loading states
        plansUpdating: state.iraPlans.status === API_STATUS.UPDATING,
        plansLoading: state.iraPlans.status === API_STATUS.PENDING,
        hasPlansError: state.iraPlans.status === API_STATUS.REJECTED,

        // Fetching functions
        getIraBeneficiaries,

        // Create, Update, Delete functions
      }}
    >
      {children}
    </IraBeneficiariesContext.Provider>
  )
}

export const useIraBeneficiaries = (): BeneficiariesProps =>
  useContext(IraBeneficiariesContext)
